import { useState } from "react";
import { IconPlus, IconX } from "@tabler/icons-react";
import { Button, Group, IconAction, Input, Stack, Text } from "@/components/ui";
import { toastError } from "@/lib/toast";

/**
 * Form de captura de motivos (lista dinámica de inputs) reutilizado por
 * los dos flujos negativos: rechazar para corrección y dar de baja
 * terminal.
 *
 * No acopla con mutations · solo recolecta los motivos válidos y los
 * entrega al caller via `onConfirmar`. Esto permite testear el form
 * por separado de los efectos.
 */
export function MotivosForm({
  variante,
  loading,
  onCancelar,
  onConfirmar,
}: {
  variante: "rechazar" | "baja";
  loading: boolean;
  onCancelar: () => void;
  onConfirmar: (motivosLimpios: string[]) => void;
}) {
  const [motivos, setMotivos] = useState<string[]>([""]);

  const submit = () => {
    const limpios = motivos.map((m) => m.trim()).filter((m) => m.length > 0);
    if (limpios.length === 0) {
      toastError({ title: "Datos incompletos", message: "Escribe al menos un motivo." });
      return;
    }
    onConfirmar(limpios);
  };

  const cambiar = (idx: number, valor: string) => {
    const next = [...motivos];
    next[idx] = valor;
    setMotivos(next);
  };

  const quitar = (idx: number) => setMotivos(motivos.filter((_, i) => i !== idx));

  return (
    <Stack gap="sm">
      <Text size="sm">
        {variante === "rechazar"
          ? "Motivos de rechazo (al menos uno · serán visibles al solicitante):"
          : "Motivos de la baja terminal (al menos uno · serán visibles al solicitante):"}
      </Text>
      <Stack gap={6}>
        {motivos.map((m, idx) => (
          <Group key={idx} gap="xs" wrap="nowrap">
            <Input
              wrapperClassName="flex-1"
              value={m}
              onChange={(e) => cambiar(idx, e.currentTarget.value)}
              placeholder={`Motivo ${idx + 1}`}
            />
            {motivos.length > 1 ? (
              <IconAction label="Quitar motivo" variant="subtle" iconColor="peligro-600" onClick={() => quitar(idx)}>
                <IconX size={14} />
              </IconAction>
            ) : null}
          </Group>
        ))}
      </Stack>
      <Group justify="space-between">
        <Button
          variant="subtle"
          size="xs"
          leftSection={<IconPlus size={12} />}
          onClick={() => setMotivos([...motivos, ""])}
        >
          Agregar motivo
        </Button>
        <Group gap="xs">
          <Button variant="default" onClick={onCancelar}>
            Cancelar
          </Button>
          <Button
            className={
              variante === "rechazar"
                ? "bg-advertencia-600 hover:bg-advertencia-700"
                : "bg-peligro-600 hover:bg-peligro-700"
            }
            loading={loading}
            onClick={submit}
          >
            {variante === "rechazar" ? "Confirmar rechazo" : "Confirmar baja"}
          </Button>
        </Group>
      </Group>
    </Stack>
  );
}
